import moment from "moment";
import { apis, get } from "./axios";
import { CLASSES } from "./constants";

export const IsDev = () => origin.includes("localhost") || origin.includes("ngrok") || origin.includes("192.168");
export const IsProd = () => origin.includes("corporate.travelwings.com");

export const getServerOrigin = () => {
	if (origin.includes("corp.twingstech.com")) {
		//Staging
		return "https://corpuat.twingstech.com";
	} else if (origin.includes("corporate.travelwings.com")) {
		//Production
		return "https://corporateapi.travelwings.com";
	}
	return "http://localhost:8080";
};

export const getGTMId = () => {
	if (origin.includes("corp.twingstech.com")) {
		//Staging
		return "GTM-NQRC7H2H";
	} else if (origin.includes("corporate.travelwings.com")) {
		//Production
		return "GTM-5NQQG98Z";
	}
	return "GTM-NQRC7H2H";
};

export const getRecaptchaSiteKey = () => {
	if (origin.includes("corp.twingstech.com")) {
		//Staging
		return "6LfFsvsqAAAAABHs12jgWjWNUaJsngV8qD0Kc6Md";
	} else if (origin.includes("corporate.travelwings.com")) {
		//Production
		return "6LcrzTcrAAAAADl7EcFqLq5fOLBvNAF0IPiYjEZr";
	}
	return "6LfFsvsqAAAAABHs12jgWjWNUaJsngV8qD0Kc6Md";
};

export const StampToTime = (stamp) => {
	return moment(stamp).format("hh:mm A");
};

export const StampTo24hr = (stamp) => {
	const data =  moment.utc(stamp).format("HH:mm");
	return data === "Invalid date" ? "" : data;
};

export const StampToDate = (stamp) => {
	if (!stamp) return "";
	return moment.utc(stamp).format("DD MMM YYYY");
};

export const removeElementAtIndex = (arr, index) => {
	return arr.filter((_, i) => i !== index);
};

export const removeElementAtIndices = (arr, indeces) => {
	return arr.filter((_, i) => !indeces.includes(i));
};

export const validateEmail = (email) => {
	const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
	return re.test(String(email).toLowerCase());
};

export const hasMinimumNameLength = (name, minLength = 2) => {
	return `${name ?? ""}`.trim().length >= minLength;
};

export const getNameValidationError = (label, name, minLength = 2) => {
	return hasMinimumNameLength(name, minLength) ? "" : `${label} must be at least ${minLength} characters`;
};

export const COMPANY_NAME_MAX_LENGTH = 100;

const COMPANY_NAME_DISALLOWED = /[^A-Za-z0-9,\- ]/g;
const COMPANY_NAME_PATTERN = /^[A-Za-z0-9,\- ]{1,100}$/;

export const sanitizeCompanyNameInput = (value) => {
	return `${value ?? ""}`.replace(COMPANY_NAME_DISALLOWED, "").replace(/^ +/, "").slice(0, COMPANY_NAME_MAX_LENGTH);
};

export const getCompanyNameValidationError = (company) => {
	const value = `${company ?? ""}`;
	if (!value) {
		return "Please enter a company name";
	}
	if (value !== value.trim()) {
		return "Company name cannot start or end with a space";
	}
	if (value.length > COMPANY_NAME_MAX_LENGTH) {
		return "Company name must be 100 characters or fewer";
	}
	if (!COMPANY_NAME_PATTERN.test(value)) {
		return "Company name can only include letters, numbers, spaces, hyphens, and commas";
	}
	return "";
};

const normalizePhoneInput = (phone) => `${phone ?? ""}`.trim();

const normalizeRegionInput = (region) => {
	if (region == null || region === "") return "";
	return `${region}`.trim();
};

export const PHONE_MIN_DIGITS = 7;
export const PHONE_MAX_DIGITS = 11;

export const limitPhoneDigits = (value) => `${value ?? ""}`.replace(/\D/g, "").slice(0, PHONE_MAX_DIGITS);

export const PHONE_VALIDATION_MESSAGES = {
	MISSING_CODE: "Please enter a phone code",
	MISSING_PHONE: "Please enter phone number",
	INVALID: "Please enter a valid phone number",
	LENGTH: "Phone number must be between 7 and 11 digits",
};

const requestPhoneNumberValid = (phone, region) =>
	new Promise((resolve) => {
		get(apis.validatePhoneNumber, { phone, region }, (response) => {
			resolve(!!response?.valid);
		});
	});

/**
 * @returns {Promise<{ ok: true } | { ok: false, reason: 'missing_phone' | 'missing_code' | 'invalid', message: string }>}
 */
export const validatePhoneNumberDetailed = async (phone, region) => {
	const p = normalizePhoneInput(phone);
	const r = normalizeRegionInput(region);
	if (!p) {
		return { ok: false, reason: "missing_phone", message: PHONE_VALIDATION_MESSAGES.MISSING_PHONE };
	}
	if (!r) {
		return { ok: false, reason: "missing_code", message: PHONE_VALIDATION_MESSAGES.MISSING_CODE };
	}
	const digits = p.replace(/\D/g, "");
	if (digits.length < PHONE_MIN_DIGITS || digits.length > PHONE_MAX_DIGITS) {
		return { ok: false, reason: "invalid_length", message: PHONE_VALIDATION_MESSAGES.LENGTH };
	}
	const valid = await requestPhoneNumberValid(p, r);
	if (!valid) {
		return { ok: false, reason: "invalid", message: PHONE_VALIDATION_MESSAGES.INVALID };
	}
	return { ok: true };
};

export const validatePhoneNumber = (phone, region) => {
	const p = normalizePhoneInput(phone);
	const r = normalizeRegionInput(region);
	if (!p || !r) return false;
	return requestPhoneNumberValid(p, r);
};

export const debounce = (fn, delay = 500) => {
	let timer;
	return (...args) => {
		clearTimeout(timer);
		timer = setTimeout(() => {
			fn(...args);
		}, delay);
	};
};

export const getAlphabetIndex = (alphabet, total) => {
	alphabet = alphabet.toLowerCase();
	const charCode = alphabet.charCodeAt(0) - 97;
	const index = charCode % total;
	return index;
};

export const removeNullKeys = (obj) => {
	return Object.keys(obj).reduce((acc, key) => {
		if (obj[key] !== null) {
			acc[key] = obj[key];
		}
		return acc;
	}, {});
};

/** First letter of each word uppercase, rest lowercase (e.g. city names from URLs). */
export const titleCaseWords = (str) => {
	if (!str?.trim()) {
		return "";
	}
	return str
		.trim()
		.split(/\s+/)
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
		.join(" ");
};

export const camelCaseToWords = (camelCaseString) => {
	const words = camelCaseString
		.replace(/([A-Z])/g, " $1")
		.trim()
		.split(" ");
	const capitalizedWords = words.map((word) => {
		return word.charAt(0).toUpperCase() + word.slice(1);
	});
	return capitalizedWords.join(" ");
};

export const getErrorMsg = (err) => {
	return err.response.data.error;
};

export const getValueFromObject = (key, object) => {
	const keys = key.split(".");
	let value = object;

	for (const key of keys) {
		if (value.hasOwnProperty(key)) {
			value = value[key];
		} else {
			return undefined;
		}
	}
	return value;
};

export const scheduleIntro = (path) => {
	let intros = localStorage.getItem("intros") || "[]";
	intros = JSON.parse(intros);
	if (!intros.includes(path)) {
		intros.push(path);
		localStorage.setItem("intros", JSON.stringify(intros));
	}
};

export const isMac = (params) => {
	if (navigator.userAgent.indexOf("Mac OS X") !== -1) {
		return true;
	} else {
		return false;
	}
};

export const dispatchCustomEvent = (eventName, data) => {
	const customEvent = new CustomEvent(eventName, {
		detail: data,
	});

	document.dispatchEvent(customEvent);
};

export const subscribeCustomEvent = (eventName, callback) => {
	const customEventListener = (event) => {
		const data = event.detail;
		callback(data);
	};

	document.addEventListener(eventName, customEventListener);

	return () => {
		document.removeEventListener(eventName, customEventListener);
	};
};
export const minutesToHrMin = (minutes) => {
	const duration = moment.duration(parseInt(minutes), "minutes");
	const hours = Math.floor(duration.asHours());
	const mins = duration.minutes(); // Remaining minutes after extracting hours
	return `${hours}hr ${mins}m`;
};

export const getTimeBetweenSegments = (segments) => {
	let time = 0;
	let prev = segments[0];
	segments.forEach((segment, i) => {
		if (i === 0) return;
		time = moment(segment.departureTime).diff(moment(prev.arrivalTime), "minutes");
		prev = segment;
	});
	return time;
};

export const TIME_SLOTS = [
	{ label: "Early Morning", value: "early", time: "3AM - 7AM", icon: "", startHour: 3, endHour: 7 },
	{ label: "Morning", value: "morning", time: "7AM - Noon", icon: "", startHour: 7, endHour: 12 },
	{ label: "Afternoon", value: "noon", time: "Noon - 4PM", icon: "", startHour: 12, endHour: 16 },
	{ label: "Evening", value: "evening", time: "4PM - 8PM", icon: "", startHour: 16, endHour: 20 },
	{ label: "Night", value: "night", time: "8PM - 3AM", icon: "", startHour: 20, endHour: 3 },
];

export const classifyTime = (time) => {
	const hourOfDay = moment.utc(time).hour();
	const slot = TIME_SLOTS.find(({ startHour, endHour }) => {
		if (startHour < endHour) {
			return hourOfDay >= startHour && hourOfDay < endHour;
		}
		return hourOfDay >= startHour || hourOfDay < endHour;
	});

	return slot?.value;
};

export const loadScript = (src) => {
	return new Promise((resolve) => {
		const script = document.createElement("script");
		script.src = src;
		script.onload = () => {
			resolve(true);
		};
		script.onerror = () => {
			resolve(false);
		};
		document.body.appendChild(script);
	});
};

export const mergeDateAndTime = (dateStamp, timeStamp) => {
	return dateStamp + (timeStamp - moment().startOf("day").toDate());
};

export const convertToHtmlElements = (html) => {
	const temp = document.createElement("div");
	temp.innerHTML = html;
	return Array.from(temp.childNodes);
};

export const convertToHtmlElement = (html) => {
	const temp = document.createElement("div");
	temp.innerHTML = html;
	const container = document.createElement("div");
	Array.from(temp.childNodes).forEach((node) => {
		container.appendChild(node.cloneNode(true));
	});
	return container;
};

export const unitToString = (unit) => {
	switch (unit) {
		case "d":
			return "days";
		case "h":
			return "hrs";
		default:
			return unit;
	}
};

export const passengerTypeFromDob = (dob, airlineCode) => {
	const age = moment().diff(moment(dob), "years");
	// Default
	if (age < 2) return "INFANT";
	if (age < 12) return "CHILD";

	//Override
	if (airlineCode === "E5" && age >= 2 && age < 14) return "CHILD";
	if (airlineCode === "3O" && age >= 2 && age < 14) return "CHILD";

	return "ADULT";
};

export const getCabinClassCode = (cabinClass) => {
	if (!cabinClass) return CLASSES[0].value; //Default to Economy
	const result = CLASSES.find((c) => c.label?.toLowerCase() === cabinClass?.toLowerCase());
	return result?.value || CLASSES[0].value;
};

// TWCP-220: Keep password guidance and validation copy in one place so every password flow stays aligned.
export const PASSWORD_POLICY_HINT =
	"Password must be at least 6 characters and include uppercase, lowercase, a number, and one special character (e.g. ! @ # $ % ^ & * _ - +).";

export const PASSWORD_POLICY_ERROR = "The password you entered doesn’t match the required criteria.";

export const PASSWORDS_DO_NOT_MATCH_ERROR = "Passwords do not match";

export const PASSWORD_CHANGED_SUCCESS = "Password changed. Please sign in again.";

export const isPasswordStrong = (password) => {
	// TWCP-183: Broaden password policy to accept a wide set of printable special characters
	// while still enforcing minimum length, mixed case, digit, and at least one special character.
	const regex =
		/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_\-+={}[\]|\\:;"'<>,.?/~`])[A-Za-z\d!@#$%^&*()_\-+={}[\]|\\:;"'<>,.?/~`]{6,}$/;
	const isMinLength = password.length >= 6;
	const hasRequiredPattern = regex.test(password);
	return isMinLength && hasRequiredPattern;
};

export const parseTerminalName = (terminal) => {
	if (!terminal) return "";
	return `Terminal ${terminal.toLowerCase().replace("terminal", "").trim().toUpperCase()}`;
};


export const formatedPrice = (price) => {
	if (price === null || price === undefined) return "0";

	const cleaned = typeof price === "string" ? price.replace(/,/g, "") : price;

	const n = Number(cleaned);
	return Number.isFinite(n) ? n.toLocaleString("en-IN") : "0";
};